import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Tag, FileText, X, Package } from 'lucide-react';
import { cartAPI } from '../services/api';
import { useCartSession } from '../hooks/useAPI';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { calculateCartTotals, calculateBestPrice, formatPrice, getNextDiscountTier, getGlobalQuantityDiscounts } from '../utils/discountUtils';
import { getImageUrlWithFallback } from '../utils/imageUtils';

// Helper function to convert price from IVA included to IVA excluded
const priceWithoutVAT = (priceWithVAT) => {
  return priceWithVAT / 1.22;
};

const Cart = () => {
  usePageTitle('Carrello');
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const sessionId = useCartSession();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [moqValidation, setMoqValidation] = useState(null); // B2B ordine minimo validation
  
  // Quote request modal state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteMinQuantity, setQuoteMinQuantity] = useState('');
  const [quoteDeliveryDate, setQuoteDeliveryDate] = useState('');
  const [requestingQuote, setRequestingQuote] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // State for global discounts
  const [globalDiscounts, setGlobalDiscounts] = useState([]);
  
  // Load global discounts on component mount
  useEffect(() => {
    const loadGlobalDiscounts = async () => {
      try {
        const discounts = await getGlobalQuantityDiscounts();
        setGlobalDiscounts(discounts);
      } catch (error) {
        console.warn('Failed to load global discounts:', error);
        // Use default discounts as fallback
        const { getDefaultQuantityDiscounts } = await import('../utils/discountUtils');
        setGlobalDiscounts(getDefaultQuantityDiscounts());
      }
    };
    
    loadGlobalDiscounts();
  }, []);

  // Calculate cart totals with cart-level quantity discounts
  const cartTotals = useMemo(() => {
    return calculateCartTotals(cartItems, globalDiscounts);
  }, [cartItems, globalDiscounts]);

  // Validate ordine minimo whenever cart changes - B2B
  useEffect(() => {
    const validateMOQ = async () => {
      if (cartItems.length === 0) {
        setMoqValidation(null);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/validate-cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: cartItems
          })
        });

        const result = await response.json();
        setMoqValidation(result);
      } catch (error) {
        console.error('Ordine minimo validation error:', error);
      }
    };

    validateMOQ();
  }, [cartItems]);

  // Load cart data on component mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        setLoading(true);
        const cartData = await cartAPI.getCart(sessionId);
        setCartItems(cartData.items || []);
        
        // Load applied coupon from sessionStorage (persist when returning from checkout)
        try {
          const storedCoupon = sessionStorage.getItem('appliedCoupon');
          if (storedCoupon) {
            const couponData = JSON.parse(storedCoupon);
            if (couponData && couponData.valid) {
              setAppliedCoupon(couponData);
              setCouponCode(couponData.coupon.code);
            }
          }
        } catch (couponError) {
          console.error('Error loading coupon from sessionStorage:', couponError);
        }
      } catch (error) {
        console.error('Failed to load cart:', error);
        toast({
          title: "Errore",
          description: "Impossibile caricare il carrello.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, [sessionId, toast]);

  // Helper function to check if an item is a sample
  const isSampleItem = (item) => {
    // Priority 1: Check if item was marked as sample when added to cart
    if (item.isSample === true) {
      console.log('✅ Item marked as SAMPLE (isSample flag):', item.productData?.name);
      return true;
    }
    
    // Priority 2: Check if item comes from samples collection (via isSamplesCollection flag)
    const isSamplesCollection = item.productData?.isSamplesCollection === true;
    
    if (isSamplesCollection) {
      console.log('✅ Item is SAMPLE (from samples collection marked in admin):', item.productData?.name);
      return true;
    }
    
    // Priority 3: Check by collection slug/name (legacy support)
    const collectionSlug = String(item.productData?.collectionName || item.productData?.collectionSlug || '').toLowerCase();
    const collectionTitle = String(item.productData?.collectionTitle || '').toLowerCase();
    
    if (collectionSlug === 'campioni' || collectionTitle.includes('campion')) {
      console.log('✅ Item is SAMPLE (from "campioni" collection by name):', item.productData?.name);
      return true;
    }
    
    // Priority 3: Check by name/format (legacy support)
    const format = String(item.productData?.format || item.format || '');
    const name = String(item.productData?.name || item.name || '');
    const description = String(item.productData?.description || item.description || '');
    const sizeClField = String(item.productData?.size_cl || item.size_cl || '');
    const selectedFormat = String(item.productData?.selectedFormat?.name || item.selectedFormat?.name || '');
    
    const isSample = format.toLowerCase().includes('5cl') || 
           format.toLowerCase().includes('campion') ||
           name.toLowerCase().includes('campion') ||
           description.toLowerCase().includes('campion') ||
           sizeClField === '5' ||
           selectedFormat.toLowerCase().includes('5cl') ||
           selectedFormat.toLowerCase().includes('campion');
    
    if (isSample) {
      console.log('✅ Item is SAMPLE (detected by name/format):', name);
    } else {
      console.log('ℹ️  Item is REGULAR PRODUCT:', name, '(collectionSlug:', collectionSlug, ')');
    }
    
    return isSample;
  };

  // Separate samples from regular products
  const sampleItems = cartItems.filter(isSampleItem);
  const regularItems = cartItems.filter(item => !isSampleItem(item));
  const hasSamples = sampleItems.length > 0;
  const hasRegularProducts = regularItems.length > 0;
  
  // Debug logging for cart state
  console.log('🛒 Cart State:', {
    totalItems: cartItems.length,
    sampleItems: sampleItems.length,
    regularItems: regularItems.length,
    hasSamples,
    hasRegularProducts
  });

  // Proceed to checkout (for samples only)
  const proceedToSamplesCheckout = () => {
    console.log('Samples checkout button clicked');
    
    if (sampleItems.length === 0) {
      toast({
        title: "Nessun Campione",
        description: "Non ci sono campioni nel carrello.",
        variant: "destructive"
      });
      return;
    }
    
    // Navigate to checkout with only samples
    navigate('/checkout?samples=true');
  };

  // Proceed to checkout
  const proceedToCheckout = () => {
    console.log('Checkout button clicked');
    
    if (cartItems.length === 0) {
      toast({
        title: "Carrello Vuoto",
        description: "Aggiungi alcuni prodotti prima di procedere al checkout.",
        variant: "destructive"
      });
      return;
    }
    
    // Store applied coupon in sessionStorage for checkout
    if (appliedCoupon) {
      sessionStorage.setItem('appliedCoupon', JSON.stringify(appliedCoupon));
    } else {
      sessionStorage.removeItem('appliedCoupon');
    }
    
    console.log('Navigating to checkout...');
    navigate('/checkout');
  };

  // Request quote from cart
  const handleRequestQuote = async () => {
    if (!user) {
      toast({
        title: "Accesso Richiesto",
        description: "Devi essere autenticato per richiedere un preventivo.",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    if (cartItems.length === 0) {
      toast({
        title: "Carrello Vuoto",
        description: "Aggiungi alcuni prodotti prima di richiedere un preventivo.",
        variant: "destructive"
      });
      return;
    }

    setRequestingQuote(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Token di autenticazione non trovato. Effettua nuovamente il login.');
      }

      const response = await fetch(`${backendUrl}/api/quotes/customer/request-from-cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cart_id: sessionId,
          customer_notes: quoteNotes,
          min_quantity_desired: quoteMinQuantity ? parseInt(quoteMinQuantity) : null,
          delivery_date_requested: quoteDeliveryDate || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Clear cart after successful quote request
        try {
          await cartAPI.clearCart(sessionId);
        } catch (clearError) {
          console.error('Error clearing cart:', clearError);
        }

        // Navigate to confirmation page
        navigate(`/quote-confirmation/${data.quote_number}`);
      } else {
        throw new Error(data.detail || 'Errore nella richiesta preventivo');
      }
    } catch (error) {
      console.error('Error requesting quote:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile inviare la richiesta. Riprova.",
        variant: "destructive"
      });
    } finally {
      setRequestingQuote(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }
    
    try {
      await cartAPI.updateCartItem(sessionId, itemId, newQuantity);
      setCartItems(cartItems.map(item => 
        item.id === itemId 
          ? { ...item, quantity: newQuantity, price: (item.price / item.quantity) * newQuantity }
          : item
      ));
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile aggiornare la quantità.",
        variant: "destructive"
      });
    }
  };

  const removeItem = async (itemId) => {
    try {
      await cartAPI.removeCartItem(sessionId, itemId);
      setCartItems(cartItems.filter(item => item.id !== itemId));
      toast({
        title: "Prodotto rimosso",
        description: "Il prodotto è stato rimosso dal carrello.",
      });
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile rimuovere il prodotto.",
        variant: "destructive"
      });
    }
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price, 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Coupon functions
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Inserisci un codice coupon');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate-coupon?code=${encodeURIComponent(couponCode)}&cart_total=${cartTotals.finalTotal}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result.valid) {
        setAppliedCoupon(result);
        setCouponError('');
        sessionStorage.setItem('appliedCoupon', JSON.stringify(result)); // Save to sessionStorage
        toast({
          title: "Coupon applicato!",
          description: result.message
        });
      } else {
        setCouponError(result.message);
        setAppliedCoupon(null);
        sessionStorage.removeItem('appliedCoupon'); // Clear if invalid
      }
    } catch (error) {
      console.error('Errore validazione coupon:', error);
      setCouponError('Errore durante la validazione del coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
    sessionStorage.removeItem('appliedCoupon'); // Clear from sessionStorage
  };

  // Calculate final total with coupon
  const finalTotalWithCoupon = appliedCoupon && appliedCoupon.valid
    ? appliedCoupon.final_total
    : cartTotals.finalTotal;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <LoadingSpinner message="Caricamento carrello..." />
        </div>
        <Footer />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Il tuo carrello è vuoto</h1>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
              Esplora le nostre collezioni premium o crea il tuo liquore personalizzato.
            </p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Link
                to="/builder"
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
              >
                Crea il Tuo Liquore
              </Link>
              <Link
                to="/campioni"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Collezioni Premium
              </Link>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium mb-8"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Continua lo shopping
        </button>

        <div className="grid lg:grid-cols-3 gap-4 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Carrello ({getTotalItems()} {getTotalItems() === 1 ? 'articolo' : 'articoli'})
                </h1>
              </div>
              
              <div className="divide-y divide-gray-200">
                {cartItems.map((item) => (
                  <div key={item.id} className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4 sm:space-x-6">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={
                            // Check for collection type and use collection product image
                            (item.type === 'collection' || item.type === 'premium-collection')
                              ? (item.productData?.collectionProductImage || 
                                 item.productData?.label?.image ||
                                 'https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=300&h=300&fit=crop')
                              : getImageUrlWithFallback(item.productData?.liquor?.image || item.liquor?.image)
                          }
                          alt="Prodotto"
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                        />
                      </div>
                      
                      {/* Product Details */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                          <div className="flex-1 min-w-0">
                            {(() => {
                              if (item.type === 'premium-collection' || item.type === 'collection') {
                                // Use product name from collection
                                const productName = item.productData?.name || item.recipe?.name || 'Prodotto Collezione';
                                const collectionTitle = item.productData?.collectionTitle || item.collection || '';
                                return (
                                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                                    {productName}{collectionTitle ? ` - ${collectionTitle}` : ''}
                                  </h3>
                                );
                              } else {
                                // Use same logic as Checkout for custom products
                                const selectedLiquor = item.productData?.liquor || item.liquor;
                                const productName = selectedLiquor?.name ? `${selectedLiquor.name} Personalizzato` : 'Amaro Personalizzato';
                                return (
                                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                                    {productName}
                                  </h3>
                                );
                              }
                            })()}
                            
                            {(item.type === 'premium-collection' || item.type === 'collection') ? (
                              <div className="mt-2 space-y-1">
                                <p className="text-sm text-gray-600 break-words">Collezione: {item.productData?.collectionTitle || item.collection}</p>
                                {/* Format Display for Collections - NEW */}
                                {(item.productData?.format || item.format) && (
                                  <p className="text-sm text-purple-600 break-words">
                                    📦 Formato: {(item.productData?.format || item.format).name} ({(item.productData?.format || item.format).size_ml}ml)
                                  </p>
                                )}
                                <p className="text-sm text-gray-600 break-words">Etichetta: {item.productData?.label?.text || item.labelText}</p>
                                <p className="text-sm text-gray-600 break-words">Bottiglia: {item.productData?.bottle?.name || item.bottle?.name}</p>
                                {/* Digital Services for Collections */}
                                {item.productData?.digitalServices && item.productData.digitalServices.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="text-sm text-purple-600 font-medium">✨ Servizi Richiesti:</p>
                                    <ul className="text-sm text-gray-600">
                                      {item.productData.digitalServices.map((service, idx) => (
                                        <li key={idx}>• {service.name}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="mt-2 space-y-1">
                                {(() => {
                                  // Use same logic as Checkout for custom products
                                  const selectedLiquor = item.productData?.liquor || item.liquor;
                                  const selectedIngredients = item.productData?.ingredients || item.ingredients || [];
                                  const selectedBottle = item.productData?.bottle || item.bottle;
                                  const labelData = item.productData?.label || item.label;
                                  
                                  return (
                                    <>
                                      {selectedLiquor && (
                                        <p className="text-sm text-gray-600 break-words">
                                          <span className="font-medium">Base:</span> {selectedLiquor.name}
                                        </p>
                                      )}
                                      
                                      {/* Format Display - NEW */}
                                      {(item.productData?.format || item.format) && (
                                        <p className="text-sm text-purple-600 break-words">
                                          <span className="font-medium">📦 Formato:</span> {(item.productData?.format || item.format).name} ({(item.productData?.format || item.format).size_ml}ml)
                                        </p>
                                      )}
                                      
                                      <p className="text-sm text-gray-600 break-words">
                                        <span className="font-medium">Botaniche:</span> {
                                          selectedIngredients.length > 0 
                                            ? selectedIngredients.map(ing => ing.name).join(', ')
                                            : 'Nessuna'
                                        }
                                      </p>
                                      
                                      {item.intensity && (
                                        <p className="text-sm text-gray-600 break-words">
                                          <span className="font-medium">Intensità:</span> Livello {item.intensity}
                                        </p>
                                      )}
                                      
                                      {labelData?.text && (
                                        <p className="text-sm text-gray-600 break-words">
                                          <span className="font-medium">Etichetta:</span> "{labelData.text}"
                                        </p>
                                      )}
                                      
                                      {labelData?.customGraphics && (
                                        <p className="text-sm text-green-600">
                                          <span className="font-medium">Design Professionale:</span> Gratuito ✓
                                        </p>
                                      )}
                                      
                                      {selectedBottle && (
                                        <p className="text-sm text-gray-600">
                                          <span className="font-medium">Bottiglia:</span> {selectedBottle.name}
                                          {selectedBottle.price > 0 && (
                                            <span className="text-blue-600 ml-1">(+€{selectedBottle.price.toFixed(2)})</span>
                                          )}
                                        </p>
                                      )}
                                      
                                      {/* Digital Services Display */}
                                      {item.productData?.digitalServices && item.productData.digitalServices.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-gray-200">
                                          <p className="text-sm text-purple-600 font-medium mb-1">
                                            ✨ Servizi Aggiuntivi Richiesti:
                                          </p>
                                          <ul className="text-sm text-gray-600 space-y-1">
                                            {item.productData.digitalServices.map((service, idx) => (
                                              <li key={idx} className="flex items-center gap-1">
                                                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                                                {service.name}
                                                {service.category && (
                                                  <span className="text-xs text-gray-400">({service.category})</span>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-red-500 hover:text-red-700 transition-colors p-2 flex-shrink-0"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3">
                          {/* Quantity Controls */}
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                              disabled={isProcessing}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const newQty = parseInt(e.target.value) || 1;
                                if (newQty > 0) {
                                  updateQuantity(item.id, newQty);
                                }
                              }}
                              onBlur={(e) => {
                                // Ensure valid quantity on blur
                                if (!e.target.value || parseInt(e.target.value) < 1) {
                                  updateQuantity(item.id, 1);
                                }
                              }}
                              className="w-20 text-center text-lg font-semibold px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              disabled={isProcessing}
                            />
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                              disabled={isProcessing}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Price with Cart-Level Discount Info */}
                          <div className="text-left sm:text-right w-full sm:w-auto">
                            {(() => {
                              // Find this item in the cart totals with applied discounts
                              // Hide prices if user is not logged in
                              if (!user) {
                                return (
                                  <div className="text-center">
                                    <p className="text-sm text-gray-500 mb-2">Registrati per vedere i prezzi</p>
                                    <button
                                      onClick={() => navigate('/register')}
                                      className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                      Registrati
                                    </button>
                                  </div>
                                );
                              }
                              
                              // Show prices ONLY for samples, hide for regular products
                              if (!isSampleItem(item)) {
                                return null; // Hide price for regular products
                              }
                              
                              // Show price for samples
                              const itemWithDiscount = cartTotals.itemsWithDiscounts?.find(
                                discountedItem => discountedItem.id === item.id
                              );
                              
                              if (itemWithDiscount) {
                                return (
                                  <div>
                                    <p className="text-base sm:text-lg font-bold text-green-600">
                                      {formatPrice(priceWithoutVAT(itemWithDiscount.finalItemTotal))}
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-500">
                                      {formatPrice(priceWithoutVAT(itemWithDiscount.finalItemTotal / item.quantity))} cad. (IVA escl.)
                                    </p>
                                  </div>
                                );
                              } else {
                                const originalPrice = item.productData?.basePrice || item.basePrice || item.price || 0;
                                return (
                                  <div>
                                    <p className="text-lg font-bold text-green-600">
                                      {formatPrice(priceWithoutVAT(originalPrice * item.quantity))}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {formatPrice(priceWithoutVAT(originalPrice))} cad. (IVA escl.)
                                    </p>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:sticky lg:top-8 overflow-hidden">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Riepilogo Ordine</h2>
              
              {/* Show registration prompt if user is not logged in */}
              {!user ? (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <svg className="w-16 h-16 text-purple-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Registrati per vedere i prezzi</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Crea un account per accedere ai prezzi B2B e agli sconti quantità riservati ai professionisti.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors mb-3"
                  >
                    Registrati Ora
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors"
                  >
                    Hai già un account? Accedi
                  </button>
                </div>
              ) : (
              <>
              {/* Quantity Discounts Section Removed */}
              
              <div className="space-y-4 mb-6">
                  {/* Coupon Section */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-gray-900">Codice Sconto</span>
                    </div>
                    
                    {!appliedCoupon ? (
                      <div>
                        <div className="flex gap-2 w-full">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => {
                              setCouponCode(e.target.value.toUpperCase());
                              setCouponError('');
                            }}
                            placeholder="INSERISCI CODICE"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm uppercase"
                            disabled={couponLoading}
                          />
                          <button
                            onClick={handleApplyCoupon}
                            disabled={couponLoading || !couponCode.trim()}
                            className="flex-shrink-0 px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                          >
                            {couponLoading ? 'Verifica...' : 'Applica'}
                          </button>
                        </div>
                        {couponError && (
                          <p className="text-red-600 text-xs mt-2 break-words">{couponError}</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 overflow-hidden">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-green-700 break-all">{appliedCoupon.coupon.code}</span>
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                {appliedCoupon.discount_type === 'percentage' 
                                  ? `-${appliedCoupon.discount_value}%`
                                  : `-€${appliedCoupon.discount_value}`}
                              </span>
                            </div>
                            <p className="text-xs text-green-600 mt-1">{appliedCoupon.message}</p>
                          </div>
                          <button
                            onClick={handleRemoveCoupon}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                {/* Riepilogo basato su tipo prodotti */}
                {hasSamples && !hasRegularProducts ? (
                  // Solo campioni - Mostra prezzi
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Package className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-green-900 mb-2">Campioni nel Carrello</h3>
                        <p className="text-sm text-green-800 mb-3">
                          Hai {sampleItems.length} {sampleItems.length === 1 ? 'campione' : 'campioni'} pronti per l'ordine.
                          Procedi al checkout per completare l'acquisto.
                        </p>
                      </div>
                    </div>
                    
                    {/* Mostra totale per campioni */}
                    <div className="mt-4 pt-3 border-t border-green-200">
                      <div className="flex justify-between text-lg font-bold text-green-900">
                        <span>Totale Campioni</span>
                        <span>{formatPrice(cartTotals.finalTotal)}</span>
                      </div>
                      <p className="text-xs text-green-700 mt-1">IVA inclusa</p>
                    </div>
                  </div>
                ) : hasSamples && hasRegularProducts ? (
                  // Entrambi - Mostra entrambe le opzioni
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <Package className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-semibold text-green-900 text-sm">Campioni: {sampleItems.length}</p>
                          <p className="text-xs text-green-700">Disponibili per ordine immediato</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-semibold text-blue-900 text-sm">Prodotti: {regularItems.length}</p>
                          <p className="text-xs text-blue-700">Richiedono preventivo personalizzato</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Solo prodotti normali - Preventivo
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <FileText className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-blue-900 mb-2">Preventivo Personalizzato</h3>
                        <p className="text-sm text-blue-800 mb-3">
                          Hai aggiunto {getTotalItems()} {getTotalItems() === 1 ? 'prodotto' : 'prodotti'} al carrello. 
                          Richiedi un preventivo personalizzato per ricevere i prezzi migliori e un'offerta dedicata.
                        </p>
                        <ul className="text-xs text-blue-700 space-y-1">
                          <li>✓ Prezzi B2B personalizzati</li>
                          <li>✓ Sconti su grandi quantità</li>
                          <li>✓ Consulenza dedicata</li>
                          <li>✓ Risposta entro 24-48 ore</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between text-gray-600 border-t pt-4">
                  <span>Totale articoli nel carrello</span>
                  <span className="font-semibold">{getTotalItems()} {getTotalItems() === 1 ? 'prodotto' : 'prodotti'}</span>
                </div>
              </div>
              
              {/* Pulsanti basati su tipo prodotti */}
              {hasSamples && !hasRegularProducts ? (
                // Solo campioni - Mostra solo checkout campioni
                <button
                  onClick={proceedToSamplesCheckout}
                  disabled={isProcessing}
                  className={`w-full mt-4 px-6 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 shadow-lg ${
                    isProcessing
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <Package className="w-5 h-5" />
                  <span>{isProcessing ? 'Elaborazione...' : 'Ordina Campioni'}</span>
                </button>
              ) : hasSamples && hasRegularProducts ? (
                // Entrambi - Mostra entrambi i pulsanti
                <div className="space-y-3 mt-4">
                  <button
                    onClick={proceedToSamplesCheckout}
                    disabled={isProcessing}
                    className={`w-full px-6 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 shadow-lg ${
                      isProcessing
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <Package className="w-5 h-5" />
                    <span>{isProcessing ? 'Elaborazione...' : 'Ordina Campioni'}</span>
                  </button>
                  
                  <button
                    onClick={() => setShowQuoteModal(true)}
                    className="w-full px-6 py-4 rounded-lg font-semibold transition-colors duration-200 bg-purple-600 text-white hover:bg-purple-700 flex items-center justify-center space-x-2"
                  >
                    <FileText className="w-5 h-5" />
                    <span>Richiedi Preventivo Prodotti</span>
                  </button>
                </div>
              ) : (
                // Solo prodotti normali - Solo preventivo
                <button
                  onClick={() => setShowQuoteModal(true)}
                  className="w-full mt-4 px-6 py-4 rounded-lg font-semibold transition-colors duration-200 bg-purple-600 text-white hover:bg-purple-700 flex items-center justify-center space-x-2 shadow-lg"
                >
                  <FileText className="w-5 h-5" />
                  <span>Richiedi Preventivo con Prezzi</span>
                </button>
              )}
              
              <div className="mt-6 text-center">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span>Pagamento sicuro garantito</span>
                </div>
              </div>
              </>
              )}
              {/* End of user logged in check */}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal Richiesta Preventivo */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Richiedi Preventivo</h2>
                </div>
                <button
                  onClick={() => setShowQuoteModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Info */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Invia una richiesta di preventivo personalizzato. Il nostro team ti risponderà entro 24-48 ore con un'offerta dedicata.
                </p>
                
                {/* Nota importante sull'offerta commerciale */}
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs text-amber-700 flex items-start">
                    <span className="mr-1">⚠️</span>
                    <span>
                      <strong>Importante:</strong> I prezzi indicati sono da considerarsi indicativi. 
                      L'offerta definitiva sarà formulata da un nostro commerciale e potrebbe subire variazioni 
                      in base alle specifiche del progetto, quantità e personalizzazioni richieste.
                    </span>
                  </p>
                </div>
                
                {hasSamples && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-xs text-blue-700 flex items-start">
                      <span className="mr-1">ℹ️</span>
                      <span>
                        <strong>Nota:</strong> I campioni nel carrello NON saranno inclusi nel preventivo. 
                        Per ordinare campioni, usa il pulsante "Ordina Campioni".
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* Note/Richieste */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note o Richieste Speciali
                  </label>
                  <textarea
                    value={quoteNotes}
                    onChange={(e) => setQuoteNotes(e.target.value)}
                    rows={4}
                    placeholder="Es: Vorrei un packaging personalizzato, consegna urgente, sconti per grandi quantità..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Quantità Minima */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantità Minima Desiderata (opzionale)
                  </label>
                  <input
                    type="number"
                    value={quoteMinQuantity}
                    onChange={(e) => setQuoteMinQuantity(e.target.value)}
                    placeholder="Es: 100"
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Data Consegna */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data Consegna Desiderata (opzionale)
                  </label>
                  <input
                    type="date"
                    value={quoteDeliveryDate}
                    onChange={(e) => setQuoteDeliveryDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Riepilogo Carrello */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Riepilogo Prodotti</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Prodotti nel carrello:</span>
                    <span className="font-medium">{cartItems.length}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowQuoteModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleRequestQuote}
                  disabled={requestingQuote}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                    requestingQuote
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {requestingQuote ? 'Invio in corso...' : 'Invia Richiesta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default Cart;